import type { Ref } from 'vue';
import { ref, computed } from 'vue';
import type { Document } from '@getaccept/lib-shared/src/types/Document';
import type { DocumentPage } from '@getaccept/lib-shared/src/types/document-page';
import { ExternalEditorType } from '@getaccept/lib-shared/src/enums/ExternalEditorType';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type { ImportData } from '../types/import-data';
import type { FileImport } from '../types/file-import';
import { ImportType } from '../types/enums/import-type';
import { ImportStatus } from '../types/enums/import-status';
import type { WordMerge } from '../../../../document-edit/assembler/types/word-merge';
import { ImporterHelper } from '../helpers/importer.helper';
import { DocumentService } from '../../../../api/documents/services/document.service';
import type { ImportTemplate } from '../types/import-template';
import type { ImportFiles } from '../types/import-files';
import { WordService } from '../../../../api/word/services/word.service';
import { ImportFilesStage } from '../types/enums/import-files-stage';
import { UploadFileService } from '../../../../api/upload-files/services/upload-file.service';
import type { UploadFileUrl } from '../../../../types/upload-file-url';
import { ImportWordDocumentStage } from '../types/enums/import-word-document-stage';
import type { WordMergeField } from '../../../../api/word/types/word-merge-field';
import { ImportGoogleDriveStage } from '../types/enums/import-google-drive-stage';
import type { ImportGoogleDrive } from '../types/import-google-drive';
import { GoogleService } from '../../../../api/google/google.service';
import type { DocumentImportData } from '../../../../document-edit/assembler/types/document-import-data';
import { AssemblerHelper } from '../../../../document-edit/assembler/helpers/assembler.helper';

export function useImporter() {
  const imported = ref(false);
  const importing = ref(false);
  const importedDocument: Ref<Document> = ref(null);
  const importedPages: Ref<DocumentPage[]> = ref([]);
  const importData: Ref<ImportData> = ref({
    title: null,
    type: null,
    error: null,
    stage: null,
  });
  const wordMerge: Ref<WordMerge> = ref({
    filename: '',
    isDynamic: true,
    missing: [],
    index: 0,
  });
  const fileImport: Ref<FileImport> = ref({
    pageCount: 0,
    fileCount: 0,
    recievedFileImportCount: 0,
  });

  const reset = () => {
    imported.value = false;
    importing.value = false;
    importedDocument.value = null;
    importedPages.value = [];
    importData.value = {
      title: null,
      type: null,
      error: null,
      stage: null,
    };
    wordMerge.value = {
      filename: '',
      isDynamic: true,
      missing: [],
      index: 0,
    };
    fileImport.value = {
      pageCount: 0,
      fileCount: 0,
      recievedFileImportCount: 0,
    };
  };

  const setImported = (value: boolean) => {
    imported.value = value;
  };

  const setImporting = (value: boolean) => {
    importing.value = value;
  };

  const setImportFailed = (error: Error) => {
    importData.value.error = error.toString();
    setImported(false);
  };

  const prepareImport = ({
    title,
    fileCount,
    type,
  }: {
    title: string;
    fileCount: number;
    type: ImportType;
  }) => {
    setImported(false);
    importData.value = {
      ...importData.value,
      type,
      title,
    };
    setFileImportProperties({ fileCount });
    setImporting(true);
  };

  const setFileImportProperties = (fileDataProperty: Partial<FileImport>) => {
    fileImport.value = { ...fileImport.value, ...fileDataProperty };
  };

  const importTemplate = async (payload: ImportTemplate) => {
    const type =
      payload.externalEditorType === ExternalEditorType.Docs
        ? ImportType.GoogleDocs
        : ImportType.Template;

    prepareImport({
      title: ImporterHelper.getImportingTemplateTitle(type, payload.name),
      fileCount: 1,
      type,
    });

    const { documentId, templateId, pageIndex } = payload;

    try {
      const document = await DocumentService.useTemplate(documentId, templateId, pageIndex);
      importedDocument.value = document;
      setImported(true);
    } catch (e) {
      setImportFailed(e);
    }
  };

  const importWordDocument = async (payload: ImportFiles) => {
    const { files, documentId, pageIndex } = payload;

    prepareImport({
      title: ImporterHelper.getImportingWordDocumentTitle(files[0]),
      fileCount: 1,
      type: ImportType.WordDocument,
    });

    try {
      const response = await WordService.upload(files[0]);
      setImportStage(ImportWordDocumentStage.Uploaded);
      processWordDocument({
        documentId,
        pageIndex,
        missing: [],
        name: response.filename,
      });
    } catch (e) {
      setImportFailed(e);
    }
  };

  const setImportStage = (
    stage: ImportFilesStage | ImportWordDocumentStage | ImportGoogleDriveStage
  ) => {
    importData.value.stage = stage;
  };

  const processWordDocument = async ({
    documentId,
    pageIndex,
    missing,
    name,
  }: {
    documentId: string;
    pageIndex: number;
    missing: WordMergeField[];
    name: string;
  }) => {
    const response = await WordService.process(documentId, name, missing, pageIndex);

    setImportStage(ImportWordDocumentStage.Processed);

    setWordMergeProperties({
      filename: response.filename,
      missing: response.missing,
      index: pageIndex,
    });

    // All ok, if status === 2 word merge fields can be filled in
    if (response.status === 1) {
      await WordService.saveImportLog(documentId);
      setImportStage(ImportWordDocumentStage.LogSaved);
    }
  };

  const setWordMergeProperties = (properties: Partial<WordMerge>) => {
    wordMerge.value = { ...wordMerge.value, ...properties };
  };

  const saveWordMergeDynamicTemplate = async ({
    documentId,
    pageIndex,
    name,
  }: {
    documentId: string;
    pageIndex: number;
    name: string;
  }) => {
    try {
      setWordMergeProperties({ missing: [] });
      const response = await WordService.saveWordTemplate(documentId, name, pageIndex);

      if (response.status === 1) {
        await WordService.saveImportLog(documentId);
        setImportStage(ImportWordDocumentStage.LogSaved);
      }
    } catch (e) {
      setImportFailed(e);
    }
  };

  const importFiles = async (payload: ImportFiles) => {
    prepareImport({
      title: ImporterHelper.getImportingFilesTitle(payload.files),
      fileCount: payload.files.length,
      type: ImportType.File,
    });

    const { documentId, files, pageIndex } = payload;
    const fileNames: string[] = files.map((file: File) => file.name);
    try {
      const uploadFileURLs: UploadFileUrl[] = await UploadFileService.getUploadFilesUrls(
        documentId,
        fileNames
      );
      setImportStage(ImportFilesStage.URLsRecieved);

      await UploadFileService.uploadFiles(uploadFileURLs, files);
      const uniqueFileNames: string[] = uploadFileURLs
        .map(({ uniqueFilename }: UploadFileUrl) => uniqueFilename)
        .reverse();
      setImportStage(ImportFilesStage.Uploading);

      await UploadFileService.uploadFilesDone(documentId, pageIndex, uniqueFileNames);
      setImportStage(ImportFilesStage.Uploaded);
    } catch (e) {
      setImportFailed(e);
    }
  };

  const startLoadingPages = (payload: DocumentImportData) => {
    setFileImportProperties({
      pageCount: fileImport.value.pageCount + payload.totalCount,
      recievedFileImportCount: ++fileImport.value.recievedFileImportCount,
    });
    if (importData.value.stage !== ImportFilesStage.ThumbnailImportStarted) {
      setImportStage(ImportFilesStage.ThumbnailImportStarted);
    }
  };

  const loadPage = async ({
    documentId,
    id,
    isGoogleDocTemplate,
  }: {
    documentId: string;
    id: string;
    isGoogleDocTemplate: boolean;
  }) => {
    try {
      if (id) {
        const page = await DocumentService.loadDocumentPage(documentId, id);
        setImportedPages([...importedPages.value, page]);
      }
      if (!ImporterHelper.pageImportDone(fileImport.value, importedPages.value?.length)) {
        return;
      }

      // We need to manually fetch Google docs templates since it can contain editor, video or content link blocks
      if (isGoogleDocTemplate) {
        const pages: DocumentPage[] = await DocumentService.loadPages(documentId);
        setImportedPages(pages);
      }

      completeFileImport(documentId);
    } catch (e) {
      setImportFailed(e);
    }
  };

  const setImportedPages = (pages: DocumentPage[]) => {
    importedPages.value = pages;
  };

  const completeFileImport = async (documentId: string) => {
    try {
      const correctedPageNumbers = await DocumentService.loadPageNums(documentId);
      const updatedPages = AssemblerHelper.updatePageNums(
        importedPages.value,
        correctedPageNumbers
      );
      setImportedPages(updatedPages);
      setImported(true);
    } catch (e) {
      setImportFailed(e);
    }
  };

  const importGoogleDrive = async (payload: ImportGoogleDrive) => {
    prepareImport({
      title: ImporterHelper.getImportingGoogleDriveTitle(payload.fileName),
      fileCount: 1,
      type: ImportType.GoogleDrive,
    });

    try {
      const data = await GoogleService.upload(payload.fileName, payload.fileUrl);
      setImportStage(ImportGoogleDriveStage.Uploaded);
      if (!data.success) {
        setImportFailed(new Error());
      } else {
        await GoogleService.save(data.filename, payload.documentId, payload.pageIndex);
        setImportStage(ImportGoogleDriveStage.Saved);
        await GoogleService.saveImportLog(payload.documentId);
        setImportStage(ImportGoogleDriveStage.LogSaved);
      }
    } catch (e) {
      setImportFailed(e);
    }
  };

  const progress = computed(() => {
    if (!importing.value) {
      return null;
    }
    if (imported.value) {
      return {
        message: t('done').toString(),
        percentage: 100,
        status: ImportStatus.Completed,
      };
    }
    const { type, error } = importData.value;
    if (error) {
      return ImporterHelper.getErrorProgress(type, error);
    }
    switch (type) {
      case ImportType.Template:
        return ImporterHelper.getTemplateProgress();
      case ImportType.File:
        return ImporterHelper.getFileProgress(
          importData.value,
          importedPages.value.length,
          fileImport.value.pageCount
        );
      case ImportType.WordDocument:
        return ImporterHelper.getWordDocumentProgress(
          importData.value,
          importedPages.value.length,
          fileImport.value.pageCount
        );
      case ImportType.GoogleDocs:
        return ImporterHelper.getGoogleDocsProgress(
          importData.value,
          importedPages.value.length,
          importedDocument.value,
          fileImport.value.pageCount
        );
      case ImportType.GoogleDrive:
        return ImporterHelper.getGoogleDriveProgress(
          importData.value,
          importedPages.value.length,
          fileImport.value.pageCount
        );
      default:
        return null;
    }
  });

  const showWordMergeModal = computed(() => !!wordMerge.value?.missing?.length);

  return {
    imported,
    importing,
    importedDocument,
    importedPages,
    importData,
    wordMerge,
    fileImport,

    reset,
    setImported,
    setImporting,
    setImportFailed,
    prepareImport,
    setFileImportProperties,
    importTemplate,
    importWordDocument,
    setImportStage,
    processWordDocument,
    setWordMergeProperties,
    saveWordMergeDynamicTemplate,
    importFiles,
    startLoadingPages,
    loadPage,
    setImportedPages,
    completeFileImport,
    importGoogleDrive,

    showWordMergeModal,
    progress,
  };
}
